import React, { useState, useEffect } from "react";
import "./loginPopup.css";
import logo from "../home/home-img/Screenshot_17-12-2024_224833_-removebg-preview (1).png";

// Loading Spinner Component
const LoadingSpinner = ({ size = "small", color = "#667eea" }) => (
  <div className={`spinner spinner-${size}`}>
    <div></div><div></div><div></div><div></div>
  </div>
);

export const LoginPopup = ({ show, onClose }) => {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneError, setPhoneError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [apiError, setApiError] = useState({ show: false, message: "", type: "" });

  // Timer for resend OTP
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  // Auto-hide API error after 5 seconds
  useEffect(() => {
    if (apiError.show) {
      const timer = setTimeout(() => {
        setApiError({ show: false, message: "", type: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [apiError.show]);

  if (!show) return null;

  // Validate phone number
  const validatePhone = (number) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(number);
  };

  // Validate OTP
  const validateOtp = (otpValue) => {
    const otpRegex = /^[0-9]{6}$/;
    return otpRegex.test(otpValue);
  };

  // Handle phone input change
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
    setPhone(value);
    setPhoneError("");
    setError("");
    setApiError({ show: false, message: "", type: "" });
  };

  // Handle OTP input change
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setOtp(value);
    setOtpError("");
    setError("");
    setApiError({ show: false, message: "", type: "" });
  };

  // Format API error message
  const formatErrorMessage = (error, defaultMessage) => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.error) return error.error;
    return defaultMessage;
  };

  // Handle API response
  const handleApiResponse = async (response) => {
    let data;
    try {
      data = await response.json();
    } catch (e) {
      // If response is not JSON
      throw new Error("Server returned an invalid response");
    }

    // Check for various error formats
    if (!response.ok) {
      const errorMessage =
        data.message ||
        data.error ||
        data.msg ||
        data.detail ||
        `Server error: ${response.status}`;
      throw new Error(errorMessage);
    }

    // Check for API-specific error codes
    if (data.status === 'error' || data.success === false) {
      throw new Error(data.message || data.error || "Request failed");
    }

    return data;
  };

  // Request OTP
  const sendOtp = async () => {
    // Clear previous errors
    setError("");
    setApiError({ show: false, message: "", type: "" });

    if (!validatePhone(phone)) {
      setPhoneError("Please enter a valid 10-digit phone number");
      return;
    }

    setLoading(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const res = await fetch(
        "https://api.aizoteq.com:3000/login/request-otp",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            username: "+91" + phone
          }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);
      console.log("api respones:", res)
      const data = await handleApiResponse(res);

      // Check for rate limiting
      if (res.status === 429) {
        throw new Error("Too many attempts. Please try again later.");
      }

      // Check if OTP was sent successfully
      if (res.ok) {
        setStep(2);
        setResendTimer(30);
        setApiError({
          show: true,
          message: data.message || "OTP sent successfully!",
          type: "success"
        });
      } else {
        throw new Error(data.message || "Failed to send OTP");
      }

    } catch (err) {
      console.error("Send OTP Error:", err);

      // Handle specific error types
      if (err.name === 'AbortError') {
        setApiError({
          show: true,
          message: "Request timeout. Please check your connection.",
          type: "error"
        });
      } else if (err.message.includes('Failed to fetch')) {
        setApiError({
          show: true,
          message: "Network error. Please check your internet connection.",
          type: "error"
        });
      } else if (err.message.includes('rate limit') || err.message.includes('too many')) {
        setApiError({
          show: true,
          message: err.message,
          type: "warning"
        });
      } else {
        setApiError({
          show: true,
          message: formatErrorMessage(err, "Failed to send OTP. Please try again."),
          type: "error"
        });
      }

      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const verifyOtp = async () => {
    // Clear previous errors
    setError("");
    setApiError({ show: false, message: "", type: "" });

    if (!validateOtp(otp)) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const res = await fetch(
        "https://api.aizoteq.com:3000/login/forgotpassword/confirm",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            username: "+91" + phone,
            otp: otp
          }),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);
      const data = await handleApiResponse(res);

      // Check for specific OTP errors
      if (res.status === 401 || data.valid === false) {
        throw new Error("Invalid OTP. Please try again.");
      }

      if (res.status === 410 || data.expired) {
        throw new Error("OTP has expired. Please request a new one.");
      }

      // Check if verification was successful
      if (res.ok) {
        console.log("user data :", data)
        // Store login data
        // Extract tokens
        const idToken = data.tokens?.IdToken;
        const accessToken = data.tokens?.AccessToken;
        const refreshToken = data.tokens?.RefreshToken;

        // Store tokens
        if (idToken) localStorage.setItem("idToken", idToken);
        if (accessToken) localStorage.setItem("accessToken", accessToken);
        if (refreshToken) localStorage.setItem("refreshToken", refreshToken);

        // Store user info
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }

        // Optional login info
        localStorage.setItem(
          "loginInfo",
          JSON.stringify({
            phone: "+91" + phone,
            loginTime: new Date().toISOString()
          })
        );

        setApiError({
          show: true,
          message: "Login successful!",
          type: "success"
        });

        // Close popup and refresh after a short delay
        setTimeout(() => {
          onClose();
          // window.location.reload();
        }, 1500);
      } else {
        throw new Error(data.message || "Verification failed");
      }

    } catch (err) {
      console.error("Verify OTP Error:", err);

      // Handle specific error types
      if (err.name === 'AbortError') {
        setApiError({
          show: true,
          message: "Request timeout. Please try again.",
          type: "error"
        });
      } else if (err.message.includes('Failed to fetch')) {
        setApiError({
          show: true,
          message: "Network error. Please check your connection.",
          type: "error"
        });
      } else if (err.message.includes('expired')) {
        setApiError({
          show: true,
          message: err.message,
          type: "warning"
        });
        // Optionally go back to step 1 after OTP expires
        setTimeout(() => {
          setStep(1);
          setOtp("");
        }, 3000);
      } else if (err.message.includes('Invalid OTP')) {
        setOtpError(err.message);
        setApiError({
          show: true,
          message: err.message,
          type: "error"
        });
      } else {
        setApiError({
          show: true,
          message: formatErrorMessage(err, "Verification failed. Please try again."),
          type: "error"
        });
      }

      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOtp = async () => {
    if (resendTimer > 0) return;
    await sendOtp();
  };

  // Go back to phone entry
  const goBack = () => {
    setStep(1);
    setOtp("");
    setError("");
    setOtpError("");
    setApiError({ show: false, message: "", type: "" });
  };

  return (
    <div className="login-overlay" onClick={onClose}>
      <div className="login-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="popup-header">
          <div className="logo-container_loginpage">
            <img
              src={logo}
              alt="Logo"
              className="logo_loginpage"
            />
          </div>
          <h2 className="popup-title">Welcome Back</h2>
          <p className="popup-subtitle">
            {step === 1
              ? "Enter your phone number to continue"
              : `Enter the 6-digit OTP sent to +91 ${phone}`}
          </p>
        </div>

        {/* API Error/Success Message */}
        {/* {apiError.show && (
          <div className={`api-message ${apiError.type}`}>
            <span className="message-icon">
              {apiError.type === 'success' && '✅'}
              {apiError.type === 'error' && '❌'}
              {apiError.type === 'warning' && '⚠️'}
              {apiError.type === 'info' && 'ℹ️'}
            </span>
            <span className="message-text">{apiError.message}</span>
            <button 
              className="message-close" 
              onClick={() => setApiError({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        )} */}

        {/* Legacy error message (kept for backward compatibility) */}
        {error && !apiError.show && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="form-container">
          {step === 1 && (
            <>
              <div className="input-group">
                <label htmlFor="phone">Phone Number</label>
                <div className="phone-input-wrapper">
                  <span className="country-code">+91</span>
                  <input
                    id="phone"
                    type="tel"
                    className={`form-input phone-input ${phoneError ? 'error' : ''}`}
                    placeholder="**** **** ****"
                    value={phone}
                    onChange={handlePhoneChange}
                    disabled={loading}
                    autoFocus
                  />
                </div>
                {phoneError && <span className="input-error">{phoneError}</span>}
              </div>

              <button
                className="submit-btn"
                onClick={sendOtp}
                disabled={loading || phone.length !== 10}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Sending OTP...</span>
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="input-group">
                <label htmlFor="otp">Enter OTP</label>
                <input
                  id="otp"
                  type="text"
                  className={`form-input ${otpError ? 'error' : ''}`}
                  placeholder="6-digit OTP"
                  value={otp}
                  onChange={handleOtpChange}
                  disabled={loading}
                  autoFocus
                  maxLength="6"
                />
                {otpError && <span className="input-error">{otpError}</span>}
              </div>

              <div className="otp-actions">
                <button
                  className="resend-btn"
                  onClick={resendOtp}
                  disabled={loading || resendTimer > 0}
                >
                  {resendTimer > 0
                    ? `Resend OTP in ${resendTimer}s`
                    : "Resend OTP"}
                </button>
                <button
                  className="back-btn"
                  onClick={goBack}
                  disabled={loading}
                >
                  Edit Number
                </button>
              </div>

              <button
                className="submit-btn"
                onClick={verifyOtp}
                disabled={loading || otp.length !== 6}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </>
          )}
        </div>

        <div className="popup-footer">
          <p className="terms-text">
            By continuing, you agree to our
            <a href="/terms_conditions.html" target="_blank" rel="noopener noreferrer"> Terms of Service</a> and
            <a href="/privacy_policy.html#privacy-policy" target="_blank" rel="noopener noreferrer"> Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPopup;